import axios, { AxiosError } from "axios";

/**
 * TSGo API client.
 *
 * • Uses REACT_APP_BACKEND_URL from env (never hardcode).
 * • Sends cookies with every request (httpOnly access/refresh tokens live there).
 * • Auto-refreshes the access token once on a 401, then retries the original request.
 */

const BASE = `${process.env.REACT_APP_BACKEND_URL ?? ""}/api`;

export const api = axios.create({
    baseURL: BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

let refreshing: Promise<void> | null = null;

async function performRefresh(): Promise<void> {
    if (refreshing) return refreshing;
    refreshing = axios
        .post(
            `${BASE}/auth/refresh`,
            {},
            { withCredentials: true },
        )
        .then(() => undefined)
        .catch(() => {
            throw new Error("refresh_failed");
        })
        .finally(() => {
            refreshing = null;
        });
    return refreshing;
}

api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const original = error.config as
            | (import("axios").InternalAxiosRequestConfig & { _retried?: boolean })
            | undefined;

        // Only try refresh once, and never for the refresh call itself.
        // `/auth/me` is the guest-detection probe — retrying it just doubles
        // the noise in devtools for logged-out users.
        if (
            error.response?.status === 401 &&
            original &&
            !original._retried &&
            !original.url?.endsWith("/auth/refresh") &&
            !original.url?.endsWith("/auth/login") &&
            !original.url?.endsWith("/auth/register") &&
            !original.url?.endsWith("/auth/me")
        ) {
            original._retried = true;
            try {
                await performRefresh();
                return api.request(original);
            } catch {
                /* swallow — fall through to reject below */
            }
        }
        return Promise.reject(error);
    },
);

// ---------------------------------------------------------------------------
// Types (mirror backend UserPublic + request payloads)
// ---------------------------------------------------------------------------

export type PublicUser = {
    id: string;
    email: string;
    full_name: string;
    phone: string;
    role:
        | "customer"
        | "admin"
        | "super_admin"
        | "operations_admin"
        | "store_admin"
        | "staff"
        | "rider"
        | string;
    store_id?: string | null;
    created_at: string;
};

export type RegisterPayload = {
    full_name: string;
    phone: string;
    email: string;
    password: string;
    confirm_password: string;
    accept_terms: boolean;
};

export type LoginPayload = {
    email: string;
    password: string;
    remember: boolean;
    /** Portal the login originated from. Backend rejects role mismatches. */
    portal?:
        | "customer"
        | "rider"
        | "staff"
        | "store"
        | "ops"
        | "superadmin";
};

// ---------------------------------------------------------------------------
// Auth API surface
// ---------------------------------------------------------------------------

export const authApi = {
    register: async (payload: RegisterPayload): Promise<PublicUser> => {
        const { data } = await api.post<PublicUser>("/auth/register", payload);
        return data;
    },

    login: async (payload: LoginPayload): Promise<PublicUser> => {
        const { data } = await api.post<PublicUser>("/auth/login", payload);
        return data;
    },

    logout: async (): Promise<void> => {
        await api.post("/auth/logout");
    },

    me: async (): Promise<PublicUser> => {
        const { data } = await api.get<PublicUser>("/auth/me");
        return data;
    },

    session: async (): Promise<{ authenticated: boolean; user: PublicUser | null }> => {
        const { data } = await api.get<{ authenticated: boolean; user: PublicUser | null }>(
            "/auth/session",
        );
        return data;
    },

    refreshSession: async (): Promise<void> => {
        // Fires the /auth/refresh endpoint using the refresh cookie. The
        // response sets a fresh access cookie; the caller re-fetches
        // /auth/session to obtain the user payload.
        await api.post("/auth/refresh");
    },

    updateProfile: async (payload: {
        full_name: string;
        phone: string;
    }): Promise<PublicUser> => {
        const { data } = await api.patch<PublicUser>("/auth/me", payload);
        return data;
    },

    forgotPassword: async (email: string): Promise<{ message: string }> => {
        const { data } = await api.post<{ message: string }>(
            "/auth/forgot-password",
            { email },
        );
        return data;
    },

    sendOtp: async (
        email: string,
        purpose: "login" | "reset",
    ): Promise<{ message: string }> => {
        const { data } = await api.post<{ message: string }>("/auth/otp/send", {
            email,
            purpose,
        });
        return data;
    },

    verifyLoginOtp: async (payload: {
        email: string;
        code: string;
        remember: boolean;
    }): Promise<PublicUser> => {
        const { data } = await api.post<PublicUser>("/auth/otp/verify-login", payload);
        return data;
    },

    verifyResetOtp: async (email: string, code: string): Promise<{ token: string }> => {
        const { data } = await api.post<{ token: string }>("/auth/otp/verify-reset", {
            email,
            code,
        });
        return data;
    },

    googleExchange: async (sessionId: string): Promise<PublicUser> => {
        const { data } = await api.post<PublicUser>("/auth/google/exchange", {
            session_id: sessionId,
        });
        return data;
    },

    resetPassword: async (
        token: string,
        password: string,
    ): Promise<{ message: string }> => {
        const { data } = await api.post<{ message: string }>(
            "/auth/reset-password",
            { token, password },
        );
        return data;
    },
};
